import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { isEmpty, isNil } from 'ramda'
import {
  Tabs,
  Tab,
  Drawer,
  AppBar,
  Toolbar,
  makeStyles,
  IconButton,
} from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'

import { checkMetrics } from 'commons/timelineAction'

import TabPanel from 'app/componentV4/Tabs/TabPanel'
import {
  ACTIVE,
  IN_ACTIVE,
  METRICS_TAB,
  MSG_DETAIL_TAB,
} from 'app/constants/messageDetail'
import {
  checkEvent,
  checkUnsend,
  checkViewIn,
  checkPublicLink,
} from 'commons/timelineAction'
import { TYPE_POLL } from 'app/constants/messageType'

import Actions from 'app/main/messages/timeline/Action/Actions'
import Metrics from 'app/main/messages/timeline/Metrics/Metrics'
import MessageDetail from 'app/main/messages/timeline/MessageDetail'
import LoaderMessageDetail from 'app/componentV4/MessageDetail/Loader/LoaderMessageDetail'
import SubmitDialog from 'app/componentV4/Dialog/SubmitDialog'
import { CANCEL } from 'app/constants/button'

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: '100%',
    overflowY: 'hidden',
    [theme.breakpoints.up('sm')]: {
      width: '85%',
    },
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '40%',
    },
  },
  appBar: {
    boxShadow: 'unset',
    borderBottom: `1px solid ${theme.palette.neutrals['200']}`,
    width: '100%',
    zIndex: 99,
    [theme.breakpoints.up('sm')]: {
      width: '85%',
    },
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '40%',
    },
  },
  toolBar: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#000000',
    backgroundColor: '#FFFFFF',
    minHeight: 'unset',
    padding: '0 12px',
    height: 'fit-content !important',
    '& .MuiTab-root': {
      minWidth: 'fit-content',
      textTransform: 'capitalize',
    },
    '& .MuiTabs-scroller > span': {
      backgroundColor: '#000000',
      height: '1px',
    },
    '& .MuiTab-textColorInherit': {
      color: theme.colors['grey-600'],
      fontWeight: '600',
    },
    '& .MuiTab-textColorInherit.Mui-selected': {
      fontWeight: 'bold',
      color: '#183560',
    },
  },
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    overflow: 'hidden',
  },
}))

const ViewDetails = (props) => {
  const {
    id,
    value,
    isOpen = false,
    onFetch,
    onClose,
    isLoading = true,
    onFetchDetail,
    handleFetchTimeline = () => {},
    onSuccseAndRedirectPage,
    isMetrics = false,
    isComment = false,
    isFavoriteLoading,
    updateFavorites,
  } = props

  const classes = useStyles()
  const { formatMessage } = useIntl()

  const [state, setState] = useState(isMetrics ? METRICS_TAB : MSG_DETAIL_TAB)
  const [isDialog, setIsDialog] = useState(false)
  const [languageId, setLanguageId] = useState(null)
  const [isOpenDetail, setIsOpenDetail] = useState(isOpen)
  const [isReloadContent, setIsReloadContent] = useState(false)
  const [isOpenConfirmClose, setiIsOpenConfirmClose] = useState(false)

  const isMetricsTab =
    checkMetrics(
      value.isMetricDisabled,
      value.status,
      value.metricDeliveryPercentage,
    ) || isMetrics

  const handleChange = async (event, newValue) => {
    if (newValue === 0) {
      await setIsOpenDetail(true)
      isEmpty(value) && !isLoading && onFetchDetail(id)
    } else await setIsOpenDetail(false)
    setState(newValue)
  }

  const handleFetch = async (id, status) => {
    await setIsOpenDetail(false)
    setState(0)
    onClose()
    handleFetchTimeline(id, status)
  }

  const handleReFetchById = async (id, status, obj = {}) => {
    onFetch(id, status, obj)
  }

  const handleFetchOtherLanguage = useCallback(
    ({ languageId, notificationId }) => {
      if (id === languageId) return
      setIsReloadContent(true)
      setLanguageId(languageId)
      onFetchDetail(notificationId, languageId)
    },
    [id, onFetchDetail],
  )

  const onCloseDetail = async () => {
    await setIsOpenDetail(false)
    if (!isDialog) onClose()
  }

  const handleCloseDetail = async () => {
    const string = localStorage.getItem('commentFormIsDirty')
    if (string === 'true') setiIsOpenConfirmClose(true)
    else onCloseDetail()
  }

  useEffect(() => {
    if (isEmpty(value)) return
    else setIsReloadContent(false)
  }, [value])

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        classes={{
          paper: classes.drawerPaper,
        }}
        onClose={handleCloseDetail}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        <AppBar position="fixed" className={classes.appBar}>
          <Toolbar className={classes.toolBar}>
            <Tabs
              value={state}
              onChange={handleChange}
              aria-label="Message Detail Tabs"
            >
              <Tab
                label={formatMessage({
                  id: 'notificationTimeline.card.metric.openRate.notification.title',
                })}
                className={clsx(
                  state === IN_ACTIVE ? 'text-blue-950' : 'text-grey-400',
                )}
                id="Message-Detail-Information"
                aria-controls="Message-Detail-Information"
              />
              {isMetricsTab ? (
                <Tab
                  className={clsx(
                    state === ACTIVE ? 'text-blue-950' : 'text-grey-400',
                  )}
                  label={formatMessage({
                    id: 'text.metrics',
                  })}
                  id="Message-Detail-Metrics"
                  aria-controls="Message-Detail-Metrics"
                />
              ) : null}
            </Tabs>
            <div className="flex">
              <div className="border-r-1 border-neutrals-200 pr-5">
                {(isLoading && !isReloadContent) ||
                state === METRICS_TAB ? null : (
                  <Actions
                    id={value.id ?? id}
                    isFavorite={value.isFavorited || false}
                    isDisplayFavorite={false}
                    isPoll={value.type === TYPE_POLL}
                    isPublicLink={checkPublicLink(
                      value.publicLink,
                      value.status,
                    )}
                    isSharingEnabled={value.isSharingEnabled}
                    isUnsend={checkUnsend(
                      value.type,
                      value.status,
                      value.channels,
                      value?.event?.status,
                    )}
                    isViewIn={checkViewIn(value.status)}
                    isEvent={checkEvent(
                      value.type,
                      value.status,
                      value?.event?.status,
                      isNil(value.scheduleCanceledDate),
                    )}
                    isDialog={(value) => setIsDialog(value)}
                    isScheduled={
                      value.isScheduled && isNil(value.scheduleCanceledDate)
                    }
                    handleFetchTimeline={handleFetch}
                    onSuccseAndRedirectPage={onSuccseAndRedirectPage}
                    onFetchDetailLanguage={handleFetchOtherLanguage}
                    scheduleCanceledDate={value.scheduleCanceledDate}
                    isReminder={value.isReminder && !isNil(value.reminder)}
                    isCommet={false}
                    isFavoriteLoading={isFavoriteLoading}
                    updateFavorites={updateFavorites}
                  />
                )}
              </div>
              <IconButton
                edge="end"
                aria-label="Close Message Detail"
                onClick={() => handleCloseDetail()}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </div>
          </Toolbar>
        </AppBar>
        {isLoading && !isReloadContent ? (
          <LoaderMessageDetail />
        ) : (
          <div
            style={{
              position: 'relative',
              marginTop: '50px',
              overflowY: 'auto',
            }}
          >
            {state === MSG_DETAIL_TAB ? (
              <TabPanel name="message-detail" value={state} index={0}>
                <MessageDetail
                  value={value}
                  isComment={isComment}
                  languageId={languageId}
                  onFetch={handleReFetchById}
                  isLoadContent={isReloadContent}
                  isOpenMessageDetail={isOpenDetail}
                  handleFetchTimeline={handleFetchTimeline}
                />
              </TabPanel>
            ) : null}

            {state === METRICS_TAB ? (
              <TabPanel name="metrics-detail" value={state} index={1}>
                <div className="p-10 ">
                  <Metrics id={value.id ?? id} />
                </div>
              </TabPanel>
            ) : null}
          </div>
        )}
      </Drawer>
      <SubmitDialog
        id="dialog-delete-team-member"
        open={isOpenConfirmClose}
        type={CANCEL}
        title={formatMessage({
          id: 'dialog.leave.title',
        })}
        description={formatMessage({
          id: 'view.detail.close.confirm.description',
        })}
        submitText={formatMessage({
          id: 'leave.label',
        })}
        onSubmit={() => onCloseDetail()}
        closeText={formatMessage({
          id: 'dialog.discard.close',
        })}
        onClose={() => setiIsOpenConfirmClose(false)}
      />
    </>
  )
}

const dataPropTypes = {
  id: PropTypes.number,
  type: PropTypes.number,
  status: PropTypes.string,
  publicLink: PropTypes.string,
  isMetricDisabled: PropTypes.bool,
  metricDeliveryPercentage: PropTypes.number,
  channels: PropTypes.arrayOf(PropTypes.number),
  event: PropTypes.shape({
    status: PropTypes.number,
  }),
}

ViewDetails.propTypes = {
  id: PropTypes.number,
  isOpen: PropTypes.bool,
  isFavoriteLoading: PropTypes.bool,
  updateFavorites: PropTypes.func,
  onClose: PropTypes.func,
  onFetch: PropTypes.func,
  isMetrics: PropTypes.bool,
  isLoading: PropTypes.bool,
  recipients: PropTypes.object,
  isComment: PropTypes.bool,
  onFetchDetail: PropTypes.func,
  handleFetchTimeline: PropTypes.func,
  onSuccseAndRedirectPage: PropTypes.func,
  value: PropTypes.shape(dataPropTypes).isRequired,
}

export default ViewDetails
